import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { waLink } from "../lib/wa";
import { useAuth } from "../auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Check, Sparkles, ShoppingBag, Newspaper, Calculator, Users, GraduationCap,
  Scale, UserCheck, Briefcase, Utensils, Image as ImageIcon, Tv, Rocket, MessageCircle, Lock } from "lucide-react";

const ICONS = {
  "shopping-bag": ShoppingBag, "newspaper": Newspaper, "calculator": Calculator, "users": Users,
  "graduation-cap": GraduationCap, "scale": Scale, "user-check": UserCheck, "briefcase": Briefcase,
  "utensils": Utensils, "image": ImageIcon, "tv": Tv, "rocket": Rocket,
};

export default function BuilderPage() {
  const location = useLocation();
  const builder_id = location.pathname.replace(/^\//, "").split("/")[0];
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [b, setB] = useState(null);
  const [oxy, setOxy] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [showcase, setShowcase] = useState([]);

  useEffect(() => {
    api.get(`/builders/${builder_id}`).then(r => {
      setB(r.data);
      if (!r.data.enabled) setNotFound("disabled");
    }).catch(() => setNotFound("missing"));
    api.get("/oxy/settings").then(r => setOxy(r.data));
    api.get("/showcase").then(r => setShowcase(r.data)).catch(() => {});
  }, [builder_id]);

  const start = () => {
    if (!b) return;
    try { sessionStorage.setItem("oxy_pending_type", b.type); } catch {}
    if (!user) { login(); return; }
    navigate(`/websites/new?type=${b.type}`);
  };

  if (notFound === "missing") return <NotEnabledScreen title="Builder Not Found" msg="This builder is not registered."/>;
  if (notFound === "disabled") return <NotEnabledScreen title="Coming Soon" msg={`${b?.name || "This builder"} isn't enabled yet. Contact admin to activate.`}/>;
  if (!b) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"/></div>;

  const Icon = ICONS[b.icon] || Rocket;
  const scForBuilder = showcase.filter(s => s.category === b.type || s.category === b.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={fileUrl(oxy.platform_logo_url) || "/oxycom-logo.jpg"} alt="OxyCom" className="w-8 h-8 rounded-full object-cover"/>
            <div className="font-black">Oxy<span className="text-sky-500">Com</span></div>
          </Link>
          <div className="flex gap-2 items-center text-sm">
            <Link to="/" className="text-slate-600 hover:text-sky-600">All builders</Link>
            {user ? (
              <Link to="/dashboard"><Button size="sm" className="bg-slate-900 hover:bg-slate-800">Dashboard</Button></Link>
            ) : (
              <Button size="sm" onClick={login} className="bg-slate-900 hover:bg-slate-800">Sign in</Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100 via-white to-emerald-100 -z-10"/>
        <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm text-xs font-semibold text-slate-600">
              <Sparkles className="w-3.5 h-3.5 text-amber-500"/> OxyCom Powered · Ready-to-sell
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              {b.name} <span className="text-sky-500">Builder</span>
            </h1>
            <p className="font-bn text-lg text-slate-600 max-w-2xl">{b.tagline_bn}</p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" onClick={start} className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 h-12 font-bold" data-testid="builder-create-btn">
                Create your {b.name} <ArrowRight className="w-5 h-5 ml-2"/>
              </Button>
              <a href={waLink(oxy.whatsapp_number, `I want to build a ${b.name} website with OxyCom`)} target="_blank" rel="noreferrer">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                  <MessageCircle className="w-4 h-4 mr-2"/>Talk to us
                </Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-sky-200/60 to-emerald-200/50 rounded-3xl blur-2xl -z-10"/>
              <Card className="p-8 bg-white shadow-2xl rounded-3xl">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white shadow-lg mb-4">
                  <Icon className="w-8 h-8"/>
                </div>
                <div className="font-bn text-lg font-bold">{b.name_bn}</div>
                <div className="text-sm text-slate-500 mt-1">Category: {b.type}</div>
                <div className="mt-4 pt-4 border-t space-y-1.5">
                  {b.features?.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0"/>{f}</div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Feature blocks */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <h2 className="text-2xl md:text-3xl font-black mb-6">Included in this builder</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {b.features?.map((f, i) => (
            <Card key={i} className="p-5 border-slate-200 hover:border-sky-300 transition">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center mb-3"><Check className="w-5 h-5"/></div>
              <div className="font-bold text-slate-800">{f}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-5xl mx-auto px-4 py-14 text-center">
        <h2 className="text-2xl md:text-3xl font-black mb-2">Simple honest pricing</h2>
        <p className="text-slate-600 font-bn">যেকোনো ধরনের ওয়েবসাইটের জন্য একই দাম</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-8 text-left">
          <Card className="p-6 border-2 border-sky-500">
            <div className="text-xs font-bold uppercase text-sky-600">Website Build (One-time)</div>
            <div className="mt-2 text-4xl font-black font-mono">৳1,499</div>
            <div className="text-sm text-slate-500">One-time · Any builder type</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>All themes + customization</li>
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>OxyCom-powered AI content</li>
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>Lifetime access</li>
            </ul>
          </Card>
          <Card className="p-6">
            <div className="text-xs font-bold uppercase text-emerald-600">Hosting</div>
            <div className="mt-2 text-4xl font-black font-mono">৳499<span className="text-lg text-slate-400">/mo</span></div>
            <div className="text-sm text-slate-500">Yearly: ৳5,988 (12 × 499)</div>
            <ul className="mt-4 space-y-1.5 text-sm">
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>Fast global hosting</li>
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>Free SSL + custom domain</li>
              <li className="flex gap-2 items-center"><Check className="w-4 h-4 text-emerald-500"/>Priority support</li>
            </ul>
          </Card>
        </div>
        <Button size="lg" onClick={start} className="mt-8 bg-sky-500 hover:bg-sky-600 rounded-full px-10 h-12">Start building</Button>
      </section>

      {/* Showcase */}
      {scForBuilder.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-3xl font-black mb-6">Live examples</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {scForBuilder.map(s => (
              <a key={s.showcase_id} href={s.url || "#"} target="_blank" rel="noreferrer" className="block">
                <Card className="overflow-hidden hover:shadow-lg transition group">
                  <div className="aspect-video bg-slate-100 bg-cover bg-center" style={{backgroundImage: s.image_url ? `url(${s.image_url})` : "none"}}/>
                  <div className="p-4">
                    <div className="font-bold">{s.title}</div>
                    <div className="text-xs text-slate-500">{s.description}</div>
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </section>
      )}

      <footer className="text-center text-sm text-slate-500 py-10 border-t bg-white">
        © 2026 OxyCom · Made in Bangladesh · Powered by OxyCom
      </footer>
    </div>
  );
}

function NotEnabledScreen({ title, msg }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="p-8 max-w-md text-center">
        <Lock className="w-12 h-12 mx-auto text-slate-400"/>
        <h2 className="mt-4 text-2xl font-black">{title}</h2>
        <p className="mt-2 text-slate-600">{msg}</p>
        <Link to="/"><Button className="mt-6 bg-sky-500 hover:bg-sky-600">Back to home</Button></Link>
      </Card>
    </div>
  );
}
